import {Customization, NewSession, Session, SessionFile, SessionFileInput, SessionFromBE} from "../types/sessionsstate";
import {authenticatedFetchBaseQuery, HttpMethod} from "./index";
import {User} from "../types/adminstate";
import {createApi} from "@reduxjs/toolkit/query/react";
import {paramsString, saveToLocalStorage, TransformedDataParams} from "../utils/commons.ts";
import {getLocale, Paginated} from "@kopjra/uikit";
import {BEPaginated} from "../types";

function transformToSession(obj: SessionFromBE): Session {
    const session: Session = {
        created: new Date(obj.created),
        end: obj.end && new Date(obj.end),
        start: obj.start && new Date(obj.start),
        id: obj.id,
        name: obj.name,
        state: obj.state,
        urls: 0,
        files: [],
        consumedCredits: obj.consumedCredits || 0,
        signingDate: obj.signingDate && new Date(obj.signingDate),
        lastHeartBeat: obj.lastHeartBeat && new Date(obj.lastHeartBeat),
        softDeleted: obj.softDeleted || false,
        customization: obj.customization,
        pdfTemplateVersion: obj.pdfTemplateVersion,
        fullProxy: obj.fullProxy || false,
    };
    if (obj.publicIp) {
        session.frameUrl = `https://${obj.dnsName}?token=${obj.token}`;
    }
    return session;
}

function transformToFile(obj: SessionFileInput): SessionFile {
    let name = "";
    if (obj.name) {
        name = obj.name.substring(obj.name.lastIndexOf("/") + 1);
    }
    return {
        category: obj.category,
        created: new Date(obj.created),
        id: obj.fileId,
        link: obj.link,
        name,
        sizeByte: obj.sizeByte,
    };
}

export const sessionApi = createApi({
    reducerPath: "sessionApi",
    baseQuery: authenticatedFetchBaseQuery(),
    tagTypes: ["Session", "SessionFile"],
    endpoints: (builder) => ({
        getSessions: builder.query<Paginated<Session>, TransformedDataParams>({
            query: (params) => `/sessions${paramsString(params)}`,
            transformResponse: (response: BEPaginated<SessionFromBE>): Paginated<Session> => ({
                items: response.results.map((o: SessionFromBE) => transformToSession(o)),
                total: response.total,
            }),
            providesTags: [{type: "Session", id: "LIST"}],
        }),
        getSession: builder.query<Session | undefined, {id?: string | number, files?: boolean}>({
            queryFn: async ({id, files = true}, _api, _extraOptions, baseQuery) => {
                if (!id) {
                    return {data: undefined};
                }
                const sessionRes = await baseQuery(`/sessions/${id}`);
                if (sessionRes.error) return sessionRes;
                const session = transformToSession(sessionRes.data as SessionFromBE);
                if (files) {
                    const filesRes = await baseQuery(`/sessions/${id}/files`);
                    if (filesRes.error) return filesRes;
                    session.files = (filesRes.data as SessionFileInput[]).map(transformToFile);
                }
                return {data: session};
            },
            providesTags: (_r, _e, arg) => [{type: "Session", id: arg.id}],
        }),
        createSession: builder.mutation<Session, {sessionInput: NewSession, customization: Customization}>({
            queryFn: async ({sessionInput, customization}, _api, _extraOptions, baseQuery) => {
                const {name, fullProxy} = sessionInput;
                if (name) {
                    const createSessionResponse = await baseQuery({
                        url: "/sessions",
                        method: HttpMethod.POST,
                        body: {name, fullProxy, description: "This is a session", locale: getLocale(), customization: {
                            fileContentOrId: customization.logoId || customization.logo?.content,
                            color: customization.color,
                            subtitle: customization.subtitle,
                        }}
                    });
                    if (createSessionResponse.error) {
                        return createSessionResponse;
                    } else {
                        const session = transformToSession(createSessionResponse.data as SessionFromBE);
                        if (session.customization && session.customization.logoFileId) {
                            customization.logoId = session.customization.logoFileId;
                        }
                        delete customization.subtitle;
                        saveToLocalStorage(customization, "customization");
                        return {data: session};
                    }
                } else {
                    return {error: {status: 400, error: "Session name is required", data: undefined}};
                }
            },
            invalidatesTags: [{type: "Session", id: "LIST"}],
        }),
        abortSession: builder.mutation<void, string | number>({
            query: (sessionId) => ({
                url: `sessionController/abortSession`,
                method: HttpMethod.POST,
                body: {sessionId},
            }),
            invalidatesTags: (_r, _e, sessionId) => [{type: "Session", id: sessionId}, {type: "Session", id: `LIST`}],
        }),
        deleteSession: builder.mutation<void, string | number>({
            query: (sessionId) => ({
                url: `/sessions/${sessionId}`,
                method: HttpMethod.DELETE,
            }),
            invalidatesTags: (_r, _e, sessionId) => [{type: "Session", id: sessionId}, {type: "Session", id: `LIST`}],
        }),
        refundSession: builder.mutation<boolean, {sessionId: string | number, credits?: number}>({
            query: ({sessionId, credits}) => ({
                url: `/sessionController/refundCredits`,
                method: HttpMethod.POST,
                body: {
                    sessionId,
                    credits,
                },
            }),
            transformResponse: (response: {refunded: boolean}): boolean => response.refunded,
            invalidatesTags: (_r, _e, {sessionId}) => [{type: "Session", id: sessionId}, {type: "Session", id: `LIST`}],
        }),
        changeOwner: builder.mutation<boolean, {sessionId: string | number, newUser: User}>({
            query: ({sessionId, newUser}) => ({
                url: `/sessionController/changeOwner`,
                method: HttpMethod.POST,
                body: {
                    sessionId,
                    userId: newUser.userId,
                },
            }),
            transformResponse: (response: {ownerChanged: boolean}): boolean => response.ownerChanged,
            invalidatesTags: (_r, _e, {sessionId}) => [{type: "Session", id: sessionId}, {type: "Session", id: `LIST`}],
        }),
        terminateVMSession: builder.mutation<boolean, string | number>({
            query: (sessionId) => ({
                url: `/sessionController/terminateVM`,
                method: HttpMethod.POST,
                body: {
                    sessionId,
                },
            }),
            transformResponse: (response: {terminated: boolean}): boolean => response.terminated,
            invalidatesTags: (_r, _e, sessionId) => [{type: "Session", id: sessionId}, {type: "Session", id: `LIST`}],
        }),
        stopVMSession: builder.mutation<boolean, string | number>({
            query: (sessionId) => ({
                url: `/sessionController/stopVM`,
                method: HttpMethod.POST,
                body: {
                    sessionId,
                },
            }),
            transformResponse: (response: {stopped: boolean}): boolean => response.stopped,
            invalidatesTags: (_r, _e, sessionId) => [{type: "Session", id: sessionId}, {type: "Session", id: `LIST`}],
        }),
        failVMSession: builder.mutation<boolean, string | number>({
            query: (sessionId) => ({
                url: `/sessionController/failVM`,
                method: HttpMethod.POST,
                body: {
                    sessionId,
                },
            }),
            transformResponse: (): boolean => true,
            invalidatesTags: (_r, _e, sessionId) => [{type: "Session", id: sessionId}, {type: "Session", id: `LIST`}],
        }),
        deleteReport: builder.mutation<void, {sessionId: string | number, locale: string}>({
            query: ({sessionId, locale}) => ({
                url: `/sessions/${sessionId}/reports/${locale}`,
                method: HttpMethod.DELETE,
            }),
        }),
        getArchiveLink: builder.mutation<string, string | number>({
            query: (sessionId) => ({
                url: `/sessions/${sessionId}/files/archive:generatePublicUrl`,
                method: HttpMethod.POST,
            }),
            transformResponse: (response: {signedUrl: string}): string => response.signedUrl,
        }),
        getReportUrl: builder.query<string, {sessionId: string | number, locale: string}>({
            query: ({sessionId, locale}) => `/sessions/${sessionId}/reports/${locale}`,
            transformResponse: (response: {url: string}): string => response.url,
        }),
        getFile: builder.query<SessionFile, {sessionId: string | number, fileId: number}>({
            query: ({sessionId, fileId}) => `/sessions/${sessionId}/files/${fileId}`,
            transformResponse: (response: SessionFileInput): SessionFile => transformToFile(response),
        }),
    }),
});

// export async function createSession(name: string, locale: string, customization: Customization, fullProxy: boolean = false): Promise<{session: Session | undefined, statusCode: number}> {
//     try {
//         const res = await apiCall({
//             urlComponent: "/sessions", method: HttpMethod.POST, body: {
//                 name,
//                 fullProxy,
//                 description: "This is a session",
//                 locale,
//                 customization: {
//                     fileContentOrId: customization.logoId || customization.logo?.content,
//                     color: customization.color,
//                     subtitle: customization.subtitle,
//                 },
//             }
//         });
//         if (!res.ok) {
//             return {session: undefined, statusCode: res.status};
//         }
//         const json = await res.json();
//         return {session: transformToSession(json), statusCode: res.status};
//     } catch {
//         return {session: undefined, statusCode: -1};
//     }
// }

// export async function getDownloadFileString(session: Session): Promise<string | undefined> {
//     const res = await apiCall({urlComponent: `/sessions/${session.id}/files`});
//     const json = await res.json();
//     if (json.length) {
//         const res1 = await apiCall({urlComponent: `/sessions/${session.id}/files/${json[0].id}`});
//         const json1 = await res1.json();
//         return json1.link;
//     } else {
//         return undefined;
//     }
// }

export const {
    useGetSessionsQuery,
    useGetSessionQuery,
    useLazyGetSessionQuery,
    useAbortSessionMutation,
    useCreateSessionMutation,
    useDeleteSessionMutation,
    useRefundSessionMutation,
    useChangeOwnerMutation,
    useTerminateVMSessionMutation,
    useStopVMSessionMutation,
    useFailVMSessionMutation,
    useDeleteReportMutation,
    useGetArchiveLinkMutation,
    useLazyGetReportUrlQuery,
    useLazyGetFileQuery,
} = sessionApi;
